import type { KollaProvidersEntityOrResourcesEntity } from 'app/services/kolla'
import { InfoCircle, LightBulbOn, RefreshDouble, WarningTriangle } from 'iconoir-react'
import { Link } from 'react-router-dom'
import ProviderPanel from 'app/components/scheduling/ProviderPanel'
import { Tooltip } from 'app/components/Tooltip'
import { FilterBy } from 'app/components/viz/FilterBy'
import { AppointmentTypeJson } from 'app/types/entities'
import { ProviderType } from 'app/types/entities'
import type { UseAvailabilityViewReturn } from './useAvailabilityView'

export type AvailabilityViewComponentProps = UseAvailabilityViewReturn

export function AvailabilityViewComponent(props: AvailabilityViewComponentProps) {
  const {
    user,
    bookableAppointmentTypes,
    operatories,
    operatoriesError,
    operatoriesSlow,
    isSyncing,
    isSyncingSchedules,
    rows,
    view,
    activeProviders,
    inactiveProviders,
    isDentrix,
    onRefreshClick,
    onSyncSchedulesClick,
    onViewChange,
    handleProviderStatusChange,
    locationOpenHours,
    isStandardMode,
  } = props

  if (!user) return null

  return (
    <div className="my-4 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <p className="text-ui-sm text-strong">Providers</p>
          <p className="rounded-full bg-blue-200 px-2 py-1 text-caption-sm !font-medium text-subtle">
            {activeProviders.length} Active
          </p>
          <p className="rounded-full bg-surface-strong px-2 py-1 text-caption-sm !font-medium text-subtle">
            {inactiveProviders.length} Inactive
          </p>
        </div>
        <div className="flex items-center gap-4">
          {user.type === `super` && (
            <button className="btn btn-sm btn-tertiary" disabled={isSyncing} onClick={() => onSyncSchedulesClick()}>
              <RefreshDouble className={isSyncingSchedules ? `animate-spin` : ``} /> <p>Sync PMS Schedules</p>
            </button>
          )}
          <Tooltip
            message={
              <div className="flex max-w-xs flex-col gap-2 p-1">
                <div className="flex items-center gap-2">
                  <LightBulbOn height={16} width={16} strokeWidth={2} className="text-ui-sm inverse-text-strong" />
                  <p className="text-ui-sm inverse-text-strong">Adding New Providers</p>
                </div>
                <p className="whitespace-pre-wrap text-body-xs inverse-text-default">
                  Providers are added as inactive by default. When new providers are added to this list please make sure
                  you are viewing "all" via the dropdown to the right.
                </p>
              </div>
            }
            // className={currentValues ? `cursor-not-allowed` : ``}
          >
            <button className="btn btn-sm btn-tertiary" disabled={isSyncing} onClick={() => onRefreshClick()}>
              <RefreshDouble className={isSyncing ? `animate-spin` : ``} /> <p>Sync Providers</p>
            </button>
          </Tooltip>
          <FilterBy
            defaultFilter={view}
            value={view}
            options={[
              { value: `all`, display: `all` },
              { value: `active`, display: `active` },
              { value: `inactive`, display: `inactive` },
            ]}
            onChange={(option) => onViewChange(option.value as `all` | `active` | `inactive`)}
          />
        </div>
      </div>
      {isStandardMode && (
        <div className="flex items-start gap-3 rounded-lg border border-default bg-surface-subtle px-4 py-3">
          <InfoCircle className="mt-0.5 h-5 w-5 shrink-0 text-subtle" />
          <p className="text-body-sm text-default">
            Hours and chairs come from your practice management system, because Availability Mode is set to Standard.
            Here you only choose which appointment types each provider can book. To set hours yourself, switch to
            Advanced in{' '}
            <Link to="../settings" relative="path" className="text-primary-600 hover:underline">
              Settings → Availability Mode
            </Link>
            .
          </p>
        </div>
      )}
      {operatoriesError && (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <WarningTriangle className="h-5 w-5 text-red-600" />
          <p className="text-body-sm text-red-800">
            Unable to load data from your Practice Management System. The PMS connection may be offline or experiencing
            issues. Provider availability settings are still visible but operatory assignments may not load.
          </p>
        </div>
      )}
      {operatoriesSlow && !operatoriesError && (
        <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <RefreshDouble className="h-5 w-5 animate-spin text-amber-600" />
          <p className="text-body-sm text-amber-800">
            Loading data from your Practice Management System is taking longer than expected. Please wait...
          </p>
        </div>
      )}
      {isStandardMode !== undefined &&
        view === `all` &&
        rows.map((provider) => (
          <ProviderPanel
            key={provider.id}
            provider={provider}
            appointmentTypes={bookableAppointmentTypes?.hits as AppointmentTypeJson[]}
            operatories={operatories as KollaProvidersEntityOrResourcesEntity[]}
            onStatusChange={(status) => handleProviderStatusChange(provider, status)}
            isSecondary={isDentrix && !provider.isPrimary}
            locationOpenHours={locationOpenHours}
            isStandardMode={isStandardMode}
            isV2
          />
        ))}
      {isStandardMode !== undefined &&
        view === `active` &&
        activeProviders.map((provider) => (
          <ProviderPanel
            key={provider.id}
            provider={provider}
            appointmentTypes={bookableAppointmentTypes?.hits as AppointmentTypeJson[]}
            operatories={operatories as KollaProvidersEntityOrResourcesEntity[]}
            onStatusChange={(status) => handleProviderStatusChange(provider, status)}
            isSecondary={isDentrix && !provider.isPrimary}
            locationOpenHours={locationOpenHours}
            isStandardMode={isStandardMode}
            isV2
          />
        ))}
      {isStandardMode !== undefined &&
        view === `inactive` &&
        inactiveProviders.map((provider) => (
          <ProviderPanel
            key={provider.id}
            provider={provider}
            appointmentTypes={bookableAppointmentTypes?.hits as AppointmentTypeJson[]}
            operatories={operatories as KollaProvidersEntityOrResourcesEntity[]}
            onStatusChange={(status) => handleProviderStatusChange(provider, status)}
            isSecondary={isDentrix && !provider.isPrimary}
            locationOpenHours={locationOpenHours}
            isStandardMode={isStandardMode}
            isV2
          />
        ))}
    </div>
  )
}
